import type { PaymentMethodTypeV2 } from '@onramp/data/graphql-types';
import { useIsWalletApp } from '@onramp/hooks/useIsWalletApp';
import { useTransferType } from '@onramp/hooks/useTransferType';
import { useBuyWidgetState } from '@onramp/state/BuyWidgetState';
import { useCountryCode } from '@onramp/state/recoil/selectors/countryCodeSelector';
import { clientSessionIdStore } from '@onramp/utils/clientSessionIdStore';
import { useKillSwitchesQuery } from '@onramp/utils/killswitches/useKillSwitches';

type RiskifiedEligibility = {
  isAuthenticated: boolean;
  isHosted: boolean;
  isBuyAndSend: boolean;
  countryCode: string | undefined;
  paymentMethodType: PaymentMethodTypeV2 | undefined;
  isLaunchEnabled: boolean;
  isKillSwitchReady: boolean;
  isKillSwitched: boolean;
};

export function isRiskifiedEligible({
  isAuthenticated,
  isHosted,
  isBuyAndSend,
  countryCode,
  paymentMethodType,
  isLaunchEnabled,
  isKillSwitchReady,
  isKillSwitched,
}: RiskifiedEligibility) {
  return (
    isAuthenticated &&
    isHosted &&
    isBuyAndSend &&
    countryCode === 'US' &&
    (paymentMethodType === 'WORLDPAY_CARD' || paymentMethodType === 'APPLE_PAY') &&
    isLaunchEnabled &&
    isKillSwitchReady &&
    !isKillSwitched
  );
}

export function useRiskifiedEligibility() {
  const countryCode = useCountryCode();
  const { isBuyAndSend } = useTransferType();
  const { sourceOfFunds } = useBuyWidgetState();
  const isWalletApp = useIsWalletApp();
  const isRiskifiedLaunchEnabled =
    clientSessionIdStore.getFeatureFlags()?.riskifiedEnabled ?? false;
  const { data: killSwitches } = useKillSwitchesQuery();

  return isRiskifiedEligible({
    isAuthenticated: Boolean(clientSessionIdStore.getUserUuid()),
    isHosted: !isWalletApp,
    isBuyAndSend,
    countryCode,
    paymentMethodType: sourceOfFunds.value?.paymentMethodType,
    isLaunchEnabled: isRiskifiedLaunchEnabled,
    isKillSwitchReady: Boolean(killSwitches),
    isKillSwitched: Boolean(killSwitches?.kill_cbpay_riskified),
  });
}
